<template>
  <div class="pdf-viewer-page">
    <!-- Toolbar -->
    <v-toolbar color="primary" dark flat elevation="2" style="height: 56px; max-height: 56px">
      <v-btn icon @click="goBack" class="ml-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="text-truncate ml-2">
        {{ filename || '附件預覽' }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon @click="downloadFile" class="mr-2" v-bind="attrs" v-on="on">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
        <span>使用此按鈕下載以保留正確的文件名</span>
      </v-tooltip>
    </v-toolbar>

    <!-- Content Area -->
    <div class="content-area">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <v-alert v-if="error" type="error" class="ma-4">
        {{ error }}
      </v-alert>

      <!-- Helpful hint banner for PDF downloads -->
      <v-banner
        v-if="!loading && !error && isPdf && showDownloadHint"
        color="info"
        dark
        class="download-hint-banner"
        single-line
      >
        <v-icon slot="icon" color="white">mdi-information</v-icon>
        提示：請使用頂部工具欄的下載按鈕以保存正確的文件名
        <template v-slot:actions>
          <v-btn text small @click="showDownloadHint = false"> 知道了 </v-btn>
        </template>
      </v-banner>

      <!-- PDF Preview with object tag for better filename handling -->
      <object
        v-if="!loading && previewUrl && isPdf"
        :data="previewUrl"
        :type="contentType || 'application/pdf'"
        class="preview-iframe"
        @error="handleIframeError"
      >
        <div class="pa-4">
          <v-alert type="warning"> 無法顯示 PDF 預覽 </v-alert>
          <v-btn color="primary" @click="downloadFile">
            <v-icon left>mdi-download</v-icon>
            下載文件
          </v-btn>
        </div>
      </object>

      <!-- Image Preview with iframe -->
      <iframe
        v-else-if="!loading && previewUrl && isImage"
        :src="previewUrl"
        class="preview-iframe"
        frameborder="0"
        @error="handleIframeError"
      ></iframe>

      <!-- Fallback for unsupported file types -->
      <div v-else-if="!loading && previewUrl" class="pa-4">
        <v-alert type="info"> 此文件類型無法預覽，請下載後查看 </v-alert>
        <v-btn color="primary" @click="downloadFile">
          <v-icon left>mdi-download</v-icon>
          下載文件
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'FileViewerPage',
  props: {
    attachmentId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      previewUrl: null,
      blobUrl: null,
      filename: this.$route.query.filename || '',
      contentType: '',
      useDirectUrl: false, // Set to true to use direct API URL instead of blob
      showDownloadHint: !localStorage.getItem('hideDownloadHint'), // Show hint unless user dismissed it
    };
  },
  computed: {
    isPdf() {
      return this.contentType?.includes('pdf') || this.filename?.toLowerCase().endsWith('.pdf');
    },
    isImage() {
      return (
        this.contentType?.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(this.filename)
      );
    },
  },
  watch: {
    showDownloadHint(newVal) {
      // Save user preference when they dismiss the hint
      if (!newVal) {
        localStorage.setItem('hideDownloadHint', 'true');
      }
    },
  },
  mounted() {
    this.loadAttachment();
  },
  methods: {
    async loadAttachment() {
      if (!this.attachmentId) {
        this.error = '無效的附件ID';
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        // Option A: Use direct URL (better for browser's built-in download)
        if (this.useDirectUrl) {
          // Get the base URL from api instance
          const baseURL = api.defaults.baseURL || '';
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');

          // Create URL with auth token
          this.previewUrl = `${baseURL}v1/attachments/${this.attachmentId}/download?token=${token}`;

          // Still fetch metadata for filename and content type
          const metaResponse = await api.head(`v1/attachments/${this.attachmentId}/download`);
          this.contentType = metaResponse.headers['content-type'] || '';

          if (!this.filename) {
            const disposition = metaResponse.headers['content-disposition'];
            if (disposition) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
              if (matches?.[1]) {
                this.filename = matches[1].replace(/['"]/g, '');
              }
            }
          }
        }
        // Option B: Use blob URL (current approach, better for security)
        else {
          const response = await api.get(`v1/attachments/${this.attachmentId}/download`, {
            responseType: 'blob',
          });

          // Get content type from response
          this.contentType = response.headers['content-type'] || '';

          // Use filename from query param if available, otherwise try to get from headers
          if (!this.filename) {
            const disposition = response.headers['content-disposition'];
            if (disposition) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
              if (matches?.[1]) {
                this.filename = matches[1].replace(/['"]/g, '');
              }
            }
          }

          // Create blob URL for preview with enhanced metadata
          const blob = new Blob([response.data], {
            type: this.contentType || 'application/octet-stream',
          });

          // Try to preserve filename in the blob (limited browser support)
          if (blob.name === undefined) {
            try {
              Object.defineProperty(blob, 'name', {
                writable: false,
                value: this.filename,
              });
            } catch (e) {
              // Some browsers don't allow this
              console.debug('Cannot set blob name:', e);
            }
          }

          this.blobUrl = URL.createObjectURL(blob);
          this.previewUrl = this.blobUrl;
        }

        // If still no filename, use default
        if (!this.filename) {
          this.filename = `attachment-${this.attachmentId}`;
        }

        // Update document title
        document.title = `${this.filename} - 附件預覽`;
      } catch (e) {
        console.error('Failed to load attachment:', e);
        this.error = '載入附件失敗，請檢查網絡連接或稍後重試';
      } finally {
        this.loading = false;
      }
    },
    downloadFile() {
      if (this.blobUrl) {
        const link = document.createElement('a');
        link.href = this.blobUrl;
        link.download = this.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    goBack() {
      // Close the window if it was opened as a new tab, otherwise go back
      if (window.history.length <= 1) {
        window.close();
      } else {
        this.$router.go(-1);
      }
    },
    handleIframeError(e) {
      console.error('Iframe loading error:', e);
      this.error = '無法載入預覽，請嘗試下載文件';
    },
  },
  beforeDestroy() {
    // Clean up blob URL to prevent memory leaks
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    // Reset document title
    document.title = 'YauOn Admin';
  },
};
</script>

<style scoped>
.pdf-viewer-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  position: relative;
  background-color: #f5f5f5;
}

.v-toolbar {
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.content-area {
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: #303030;
}

.download-hint-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  margin: 0;
  border-radius: 0;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  background-color: #ffffff;
}

/* Loading overlay styles */
.v-overlay {
  z-index: 100;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .v-toolbar-title {
    font-size: 0.9rem;
  }

  .download-hint-banner {
    font-size: 0.85rem;
  }
}
</style>
